/**
 * Oath Settings Context
 * Provides user settings state and methods to the OATH app routes
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAllSettings,
  getSettingTypes,
  updateSetting,
  resetAllSettings,
  parseBooleanSetting,
  parseIntegerSetting,
  getEffectiveValue,
  SettingKeys,
} from './oathUserSettingsService';
import { useOathAuth } from './oathAuthContext';

const OathSettingsContext = createContext(null);

/**
 * Hook to access the Oath settings context
 * @returns {OathSettingsContextValue}
 */
export const useOathSettings = () => {
  const context = useContext(OathSettingsContext);
  if (!context) {
    throw new Error('useOathSettings must be used within an OathSettingsProvider');
  }
  return context;
};

/**
 * @typedef {Object} OathSettingsContextValue
 * @property {Object} settings - All user settings grouped by category
 * @property {boolean} isLoading - Whether settings are being loaded
 * @property {boolean} isDarkMode - Whether dark mode is enabled
 * @property {(key: string, value: any) => Promise<{success: boolean, error?: string}>} updateSetting
 * @property {() => Promise<{success: boolean, error?: string}>} resetSettings
 * @property {() => Promise<void>} refreshSettings
 * @property {(key: string) => any} getSettingValue
 * @property {(key: string) => boolean} getBooleanSetting
 * @property {(key: string) => number} getIntegerSetting
 */

/**
 * Apply dark mode class to document
 * @param {boolean} isDark
 */
const applyDarkMode = (isDark) => {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

export const OathSettingsProvider = ({ children }) => {
  const { isAuthenticated, user } = useOathAuth();
  const [settings, setSettings] = useState({});
  const [settingTypes, setSettingTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Compute dark mode from settings
  // API returns 'settingKey' property
  const isDarkMode = parseBooleanSetting(
    settings?.APPEARANCE?.find((s) => s.settingKey === SettingKeys.DARK_MODE)
  );

  // Apply dark mode whenever it changes
  useEffect(() => {
    applyDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Clean up dark mode class on unmount or when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      applyDarkMode(false);
    }
  }, [isAuthenticated]);

  // Load settings and setting types when user is authenticated
  useEffect(() => {
    const loadSettings = async () => {
      if (!isAuthenticated) {
        setSettings({});
        setSettingTypes([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const [settingsResult, typesResult] = await Promise.all([
        getAllSettings(),
        getSettingTypes(),
      ]);

      if (settingsResult.success) {
        setSettings(settingsResult.settings || {});
      } else {
        console.error('Failed to load settings:', settingsResult.error);
        setSettings({});
      }

      if (typesResult.success) {
        setSettingTypes(Array.isArray(typesResult.data) ? typesResult.data : []);
      } else {
        console.error('Failed to load setting types:', typesResult.error);
      }

      setIsLoading(false);
    };

    loadSettings();
  }, [isAuthenticated, user?.userId]);

  /**
   * Get a setting object by key from any category
   * API returns 'settingKey' property
   */
  const findSettingByKey = useCallback(
    (key) => {
      for (const category of Object.values(settings)) {
        if (Array.isArray(category)) {
          const setting = category.find((s) => s.settingKey === key);
          if (setting) return setting;
        }
      }
      return null;
    },
    [settings]
  );

  /**
   * Get the effective value of a setting
   */
  const getSettingValue = useCallback(
    (key) => {
      const setting = findSettingByKey(key);
      return getEffectiveValue(setting);
    },
    [findSettingByKey]
  );

  /**
   * Get a boolean setting value
   */
  const getBooleanSetting = useCallback(
    (key) => {
      const setting = findSettingByKey(key);
      return parseBooleanSetting(setting);
    },
    [findSettingByKey]
  );

  /**
   * Get an integer setting value
   */
  const getIntegerSetting = useCallback(
    (key) => {
      const setting = findSettingByKey(key);
      return parseIntegerSetting(setting);
    },
    [findSettingByKey]
  );

  /**
   * Update a single setting
   */
  const handleUpdateSetting = useCallback(
    async (key, value) => {
      const result = await updateSetting(key, value);

      if (result.success) {
        // Refresh all settings to ensure consistency
        const refreshResult = await getAllSettings();
        if (refreshResult.success) {
          setSettings(refreshResult.settings || {});
        }
      }

      return result;
    },
    []
  );

  /**
   * Reset all settings to defaults
   */
  const handleResetSettings = useCallback(async () => {
    const result = await resetAllSettings();

    if (result.success) {
      // Reload settings after reset
      const refreshResult = await getAllSettings();
      if (refreshResult.success) {
        setSettings(refreshResult.settings || {});
      }
    }

    return result;
  }, []);

  /**
   * Manually refresh settings from server
   */
  const refreshSettings = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    const result = await getAllSettings();

    if (result.success) {
      setSettings(result.settings || {});
    }

    setIsLoading(false);
  }, [isAuthenticated]);

  // Group setting types by category (sorted by displayOrder within each)
  const settingTypesByCategory = useMemo(() => {
    const grouped = {};
    for (const type of settingTypes) {
      const cat = type.category || 'OTHER';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(type);
    }
    // Sort within each category by displayOrder
    for (const cat of Object.keys(grouped)) {
      grouped[cat].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }
    return grouped;
  }, [settingTypes]);

  const value = {
    settings,
    settingTypes,
    settingTypesByCategory,
    isLoading,
    isDarkMode,
    updateSetting: handleUpdateSetting,
    resetSettings: handleResetSettings,
    refreshSettings,
    getSettingValue,
    getBooleanSetting,
    getIntegerSetting,
  };

  return (
    <OathSettingsContext.Provider value={value}>
      {children}
    </OathSettingsContext.Provider>
  );
};

export default OathSettingsContext;
